/**
 * Quantum Design System Settings Manager
 *
 * This module provides utilities for saving and retrieving user settings
 * across different applications using the Quantum Design System.
 * Settings are stored as cookies on the top-level domain to enable
 * sharing across subdomains, with fallback to localStorage for development.
 */

/**
 * Settings management for the Quantum Design System
 * Provides methods to save and retrieve user preferences
 */
export class QdsSettings {
  private _prefix: string;
  private _domain: string;
  private _path: string;
  private _sameSite: string;

  /**
   * Creates a new settings manager instance
   */
  constructor() {
    this._prefix = 'qds.';
    this._domain = '.q360.ai';
    this._path = '/';
    this._sameSite = 'Lax';
  }

  /**
   * Parses stored setting values while remaining tolerant of legacy raw strings.
   *
   * @param {string} value - The stored value to parse
   * @returns {any} The parsed value
   */
  private _parseStoredValue<T>(value: string): T | string {
    try {
      return JSON.parse(value) as T;
    } catch {
      return value;
    }
  }

  /**
   * Determines if we're on a q360.ai domain
   * Ignores port number when checking domain
   * 
   * @returns {boolean} True if on a q360.ai domain
   */
  private _isQ360Domain(): boolean {
    // Extract hostname without port
    const hostname = window.location.hostname;
    return hostname.endsWith('q360.ai');
  }

  /**
   * Saves a setting with the given name and value
   * Automatically falls back to localStorage when not on q360.ai domain
   * 
   * @param {string} name - The setting name
   * @param {any} value - The setting value (will be JSON serialized)
   * @returns {boolean} True if the setting was saved successfully
   */
  saveSetting(name: string, value: any): boolean {
    try {
      // Create prefixed name
      const prefixedName = this._prefix + name;
      
      // Serialize the value
      const serializedValue = JSON.stringify(value);
      
      // Check if we're on a q360.ai domain
      if (this._isQ360Domain()) {
        // Use cookie-based storage
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        
        // Create cookie string
        const cookieString = `${prefixedName}=${encodeURIComponent(serializedValue)}; expires=${expiryDate.toUTCString()}; path=${this._path}; domain=${this._domain}; SameSite=${this._sameSite}`;
        
        // Set the cookie
        document.cookie = cookieString;
      } else {
        // Fall back to localStorage for development environments
        localStorage.setItem(prefixedName, serializedValue);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save setting:', error);
      return false;
    }
  }

  /**
   * Retrieves a setting with the given name
   * Automatically falls back to localStorage when not on q360.ai domain
   * 
   * @param {string} name - The setting name
   * @param {any} defaultValue - The default value to return if the setting is not found
   * @returns {any} The setting value, or defaultValue if not found
   */
  getSetting<T>(name: string, defaultValue: T | null = null): T | null {
    try {
      // Create prefixed name
      const prefixedName = this._prefix + name;
      
      // Check if we're on a q360.ai domain
      if (this._isQ360Domain()) {
        // Get all cookies
        const cookies = document.cookie.split(';');
        
        // Find matching cookie
        for (const cookie of cookies) {
          const [cookieName, cookieValue] = cookie.trim().split('=');
          
          if (cookieName === prefixedName && cookieValue) {
            // Deserialize and return the value
            return this._parseStoredValue<T>(decodeURIComponent(cookieValue)) as T;
          }
        }
      } else {
        // Fall back to localStorage for development environments
        const value = localStorage.getItem(prefixedName);
        
        if (value !== null) {
          return this._parseStoredValue<T>(value) as T;
        }
      }
      
      // Return default value if not found
      return defaultValue;
    } catch (error) {
      console.error('Failed to retrieve setting:', error);
      return defaultValue;
    }
  }
}
